import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import type { Task } from '@klareospace/pharos-lib-types';
import { Skeleton } from '@klareospace/pharos-lib-web-utils';

import { flattenJobProofs, ProofsTable, ProofViewDialog, useProofViewDialog } from '&/features/tasks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderProofsTabProps {
  readonly isLoading: boolean;
  readonly tasks: Task[] | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PROOFS tab body in `OrderDetailDrawer` — one card per task (same ordering as
 * `TasksSection`), each passing its own "Task X of Y" header into `ProofsTable`
 * so the card look (border, rounding) stays defined once inside that shared
 * component. Guarded defensively by `orders:list`, mirroring
 * `TaskDetailDialog`'s gating even though the tab is only reachable from the
 * already-gated orders list.
 */
export function OrderProofsTab({ isLoading, tasks }: OrderProofsTabProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { onOpenChange, openProofView, selectedProof } = useProofViewDialog();

  if (isLoading) {
    return <Skeleton className="h-12 w-full rounded-xl" />;
  }

  const total = tasks?.length ?? 0;

  return (
    <RequirePermission permission="orders:list">
      <div className="space-y-4">
        {total === 0 ? (
          // No tasks at all — ProofsTable's own early-return renders the single "no proof" empty state.
          <ProofsTable onView={openProofView} proofs={[]} />
        ) : (
          tasks?.map((task, index) => (
            <ProofsTable
              header={
                <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('drawer.proofs.taskXofY', { current: index + 1, total })}
                  </span>
                  <span className="truncate text-xs font-medium text-foreground">
                    {t(`tasks:detail.type.${task.type}`)}
                  </span>
                </div>
              }
              key={task.id}
              onView={openProofView}
              proofs={flattenJobProofs(task.jobs)}
            />
          ))
        )}
      </div>
      <ProofViewDialog jobProof={selectedProof} onOpenChange={onOpenChange} />
    </RequirePermission>
  );
}
